import React, { useEffect, useState } from 'react';
import { getCaseHistory, getDocumentDownload } from '../api/admin';
import { useLanguage } from '../i18n/LanguageContext';

// Permissions & History Development Plan (13.10.2026) — "Case Control ›
// History": only the status "Done" (completed) ever lands here, and it
// shows up at once -- it's the same completed-assignment row the moment
// the Owner records "Accepted". Reads the actual assignees from the
// assignment events server-side, so an earlier assignee on a reassigned
// case still sees it here even though someone else finished it.
const CASE_TYPE_LABELS = { damage_case: 'Damage claim', application: 'Provider application', support_ticket: 'Support ticket', verification: 'Identity verification', incident: 'Incident report' };

async function downloadDocument(documentId) {
  try {
    const res = await getDocumentDownload(documentId);
    window.open(res.data.url, '_blank');
  } catch (err) {
    alert(err.response?.data?.error || 'Document is not ready yet.');
  }
}

const CaseControlHistory = () => {
  const { t } = useLanguage();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canViewOthers, setCanViewOthers] = useState(false);

  useEffect(() => {
    getCaseHistory().then((res) => {
      setCases(res.data?.cases || []);
      setCanViewOthers(!!res.data?.can_view_others);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <div style={s.page}>
      <div style={s.breadcrumb}>{t('Case Management')} › {t('My History')}</div>
      <h1 style={s.h1}>{t('My History')}</h1>
      <p style={s.hint}>
        Completed assignments ("Done"). This is never deleted.
        {canViewOthers ? ' You can see everyone’s completed assignments.' : ' Showing only the cases you yourself worked on.'}
      </p>

      {loading ? <p>Loading…</p> : (
        <table style={s.table}>
          <thead><tr>
            <th style={s.th}>{t('Country')}</th><th style={s.th}>{t('Case')}</th><th style={s.th}>{t('Type')}</th>
            <th style={s.th}>{t('Decision')}</th><th style={s.th}>{t('Decided')}</th><th style={s.th}>{t('Documents')}</th>
          </tr></thead>
          <tbody>
            {cases.map((c) => (
              <tr key={`${c.case_type}:${c.case_id}`}>
                <td style={s.td}>{c.country_code || '—'}</td>
                <td style={s.td}>{c.display_id} <span style={s.titleSmall}>{c.title}</span></td>
                <td style={s.td}>{CASE_TYPE_LABELS[c.case_type]}</td>
                <td style={s.td}><span style={s.badge}>{c.assignment?.owner_decision === 'accepted' ? t('Accepted') : (c.assignment?.owner_decision || '—')}</span></td>
                <td style={s.td}>{c.assignment?.decided_at ? new Date(c.assignment.decided_at).toLocaleString() : '—'}</td>
                <td style={s.td}>
                  {c.assignment?.order_document_id && <button style={s.btnGray} onClick={() => downloadDocument(c.assignment.order_document_id)}>{t('Order Document')}</button>}
                  {c.assignment?.delivery_note_id && <button style={s.btnGray} onClick={() => downloadDocument(c.assignment.delivery_note_id)}>{t('Delivery Note')}</button>}
                  {!c.assignment?.order_document_id && !c.assignment?.delivery_note_id && <span style={s.muted}>{t('Document being generated')}</span>}
                </td>
              </tr>
            ))}
            {cases.length === 0 && <tr><td style={s.td} colSpan={6}>{t('No completed assignments yet.')}</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
};

const s = {
  page: { padding: 24, maxWidth: 1100 },
  breadcrumb: { fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 },
  h1: { fontSize: 22, fontWeight: 800, marginBottom: 8 },
  hint: { color: '#6b7280', fontSize: 13, marginBottom: 16, maxWidth: 700 },
  table: { width: '100%', borderCollapse: 'collapse', background: 'white', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: 12 },
  td: { padding: '8px 10px', borderBottom: '1px solid #f3f4f6' },
  titleSmall: { color: '#9ca3af', fontSize: 11, display: 'block' },
  muted: { color: '#9ca3af', fontSize: 12 },
  badge: { padding: '3px 8px', borderRadius: 999, background: '#dcfce7', color: '#166534', fontSize: 11, fontWeight: 700 },
  btnGray: { padding: '5px 10px', marginRight: 4, borderRadius: 6, border: '1px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer', fontSize: 12 },
};

export default CaseControlHistory;
